/**
 * settings/types.ts — typed user preferences (v2M4).
 *
 * The Settings type is structured so each future tab in UI_UX §10 owns a
 * sub-object (gameplay, audio, graphics, controls, accessibility, interface).
 * v2M4 ships only `gameplay`; the others are reserved keys that later
 * milestones populate when their backing systems land (M15 graphics,
 * M16 audio, etc.).
 *
 * Persistence shape is identical to runtime shape — no separate
 * "serialised" form is needed because every field is JSON-safe.
 *
 * Schema versioning: bump SETTINGS_SCHEMA_VERSION whenever the shape
 * changes incompatibly. Storage handles version mismatches by falling
 * back to defaults rather than attempting migration (settings are user
 * preferences; recreating them is cheap).
 */

import type { UserSpeed } from '../game/speed-controller';

/**
 * Bumped to 2 in v2M8.8 when AudioSettings landed. Bumped to 3 in
 * v3M19.3 when GameplaySettings.showDamageNumbers landed. Storage
 * falls back to defaults on schema mismatch — no migration is attempted
 * because settings are cheap user preferences, not load-bearing state.
 */
export const SETTINGS_SCHEMA_VERSION = 3;

export interface GameplaySettings {
  /**
   * Initial sim speed for a new or loaded game. Read by main.ts when
   * starting an in-game session and used to seed the SpeedController.
   */
  defaultGameSpeed: UserSpeed;
  /**
   * Whether the in-game-menu and main-menu Exit actions show a
   * confirm() dialog before tearing down.
   */
  confirmExit: boolean;
  /**
   * Whether the in-game-menu "Return to Main Menu" action shows a
   * confirm() dialog before discarding the current game.
   */
  confirmReturnToMainMenu: boolean;
  /**
   * v3M19.3 — whether the floating damage-number VFX render in combat.
   * When false, the renderer skips draining `gameState.combatEvents`;
   * the sim still publishes events (cheap) but no FloatingTextVfx is
   * scheduled. Default `true` so out-of-the-box players see the new
   * combat readout; can be toggled in the Settings → Gameplay → Combat
   * VFX section.
   */
  showDamageNumbers: boolean;
}

/**
 * v2M8.8 — audio bus volumes (AUDIO §3.1 + §11). All four are 0..1
 * multipliers applied to the corresponding gain node:
 *   - master: global volume, on top of music / sfx / ui
 *   - music:  music bus (ambient bed today, more in v3M11)
 *   - sfx:    gameplay SFX bus (weapons, ship lifecycle, structures)
 *   - ui:     UI SFX bus (clicks, hovers, panel transitions, alerts)
 *
 * Per-call attenuation (positional / volume ducking) multiplies on
 * top of these. Master-mute hotkey M and focus-loss fade live in the
 * AudioEngine — they don't touch these slider values.
 */
export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  ui: number;
}

/**
 * Top-level Settings record. Future-tab sub-objects (graphics,
 * controls, accessibility, interface) become required fields when
 * their milestones ship; for v2M8.8 only gameplay + audio are
 * populated, others remain absent.
 */
export interface Settings {
  schemaVersion: number;
  gameplay: GameplaySettings;
  audio: AudioSettings;
}

export const DEFAULT_GAMEPLAY_SETTINGS: GameplaySettings = {
  defaultGameSpeed: 1,
  confirmExit: true,
  confirmReturnToMainMenu: true,
  showDamageNumbers: true,
};

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  master: 0.7,
  music: 0.5,
  sfx: 0.7,
  ui: 0.7,
};

export const DEFAULT_SETTINGS: Settings = {
  schemaVersion: SETTINGS_SCHEMA_VERSION,
  gameplay: { ...DEFAULT_GAMEPLAY_SETTINGS },
  audio: { ...DEFAULT_AUDIO_SETTINGS },
};
